import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroParticles from "@/components/HeroParticles";
import Reveal, { MaskedLine, EASE } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { API } from "@/lib/api";
import { SERVICES, MARQUEE_ITEMS, CHAPTERS, IMAGES, MEDIA_ITEMS } from "@/data/content";

const TETRIS_SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];

export default function Home() {
  const heroRef = useRef(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Subscribed", { description: "You'll receive the next Axiom Brief." });
      setNewsletterEmail("");
    } catch {
      toast.error("Could not subscribe", { description: "Please try again in a moment." });
    } finally {
      setSubscribing(false);
    }
  };

  const [media, setMedia] = useState(MEDIA_ITEMS);

  useEffect(() => {
    axios.get(`${API}/media`)
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setMedia(r.data); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <HeroParticles />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-36 pb-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-xs uppercase tracking-[0.4em] text-cobalt font-medium"
              data-testid="hero-eyebrow"
            >
              Axiom Computers · Consultancy &amp; Research
            </motion.p>
            <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink" data-testid="hero-heading">
              <MaskedLine delay={0.15}>Intelligence,</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-cobalt">engineered</em> for
              </MaskedLine>
              <MaskedLine delay={0.45}>the real world.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-8 max-w-xl text-base md:text-lg text-ink/60 leading-relaxed"
              data-testid="hero-subtext"
            >
              An independent AI consultancy for business. We find where artificial intelligence
              saves you time and money — then set it up, train your team, and measure the results.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-7 py-3.5 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98]"
              >
                Start a project <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink px-7 py-3.5 text-sm tracking-wide transition-colors duration-200 hover:border-cobalt hover:text-cobalt"
              >
                Explore services
              </Link>
            </motion.div>
          </div>

          <div className="md:col-span-5 relative hidden md:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-cobalt/5 rounded-full blur-3xl" aria-hidden="true" />
              <div className="relative overflow-hidden border border-ink/10 aspect-[4/5]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Abstract neural lattice — Axiom research visual"
                  style={{ y: imgY }}
                  className="w-full h-[115%] object-cover"
                  data-testid="hero-image"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.3em] text-ink/40 flex justify-between">
                <span>Neural lattice study</span>
                <span>Axiom Computers</span>
              </p>
            </motion.div>
          </div>
        </div>

      </section>

      <section className="border-y border-ink/10 bg-[#EFF1F4] py-7 overflow-hidden" data-testid="editorial-marquee">
        <Marquee speed={28} gradient={false} pauseOnHover>
          {MARQUEE_ITEMS.map((item) => (
            <span key={item} className="mx-14 flex items-center gap-28 text-sm uppercase tracking-[0.35em] text-ink/55">
              {item}
              <span className="text-cobalt text-xs">◆</span>
            </span>
          ))}
        </Marquee>
      </section>

      <section className="py-24 md:py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Manifesto" title="Three commitments. One standard of proof." />
          <div className="mt-16 border-t border-ink/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-14 border-b border-ink/10" data-testid={`manifesto-chapter-${c.num}`}>
                  <div className="md:col-span-2">
                    <span className="font-display text-5xl md:text-6xl text-cobalt/70">{c.num}</span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="font-display text-3xl md:text-4xl tracking-tight text-ink">{c.title}</h3>
                  </div>
                  <div className="md:col-span-5 md:col-start-8">
                    <p className="text-ink/60 text-base md:text-lg leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white border-y border-ink/10" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="How we help" title="Everyday AI, made genuinely useful." />
            <Reveal delay={0.15}>
              <Link
                to="/services"
                data-testid="services-preview-link"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-cobalt hover:gap-4 transition-[gap] duration-300"
              >
                All services <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            {SERVICES.slice(0, 4).map((s, i) => (
              <Reveal key={s.num} delay={i * 0.07} className={TETRIS_SPANS[i]}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.num}`}
                  className="group block h-full border border-ink/10 bg-paper p-8 md:p-10 transition-transform duration-300 hover:-translate-y-1.5"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-xs tracking-[0.3em] text-ink/40">{s.num}</span>
                    <ArrowUpRight size={20} className="text-ink/30 transition-colors duration-300 group-hover:text-cobalt" />
                  </div>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl tracking-tight text-ink group-hover:text-cobalt transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-ink/60 leading-relaxed line-clamp-3">{s.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="media-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Media" title="News and updates from the firm." />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {media.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.07} className="bg-paper">
                <Link to={`/news/${m.id}`} className="group block p-8 md:p-10 h-full" data-testid={`media-item-${i}`}>
                  <p className="text-xs uppercase tracking-[0.25em] text-cobalt">{m.date}</p>
                  <h3 className="mt-4 font-display text-2xl text-ink leading-snug group-hover:text-cobalt transition-colors duration-300">{m.title}</h3>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed">{m.body}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cobalt">
                    Read article <ArrowUpRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-paper py-24 md:py-28" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.35em] text-cobalt font-medium">Newsletter</p>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight leading-tight">
                The Axiom Brief — practical AI notes for business, once a month.
              </h2>
              <p className="mt-5 text-paper/60 max-w-xl leading-relaxed">
                No hype, no spam. One short email each month with what we&rsquo;re learning from real
                client work — and what we&rsquo;d ignore.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-paper/5 border border-paper/20 rounded-full px-5 py-3.5 text-paper placeholder:text-paper/40 focus:outline-none focus:ring-2 focus:ring-cobalt"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-subscribe-button"
                  className="rounded-full bg-paper text-ink px-7 py-3.5 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:text-paper hover:scale-[0.98] disabled:opacity-50"
                >
                  {subscribing ? "Subscribing…" : "Subscribe"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <Reveal>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight text-ink leading-[1.1]">
                The hard problem<br />is the <em className="text-cobalt">interesting</em> one.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Reveal delay={0.15}>
              <Link
                to="/contact"
                data-testid="home-cta-button"
                className="inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98]"
              >
                Start a conversation <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
